import PropTypes from 'prop-types'

import styled, { css } from 'styled-components'

import ColumnGridPanel from 'components/ColumnGridPanel'

import { degrees, degreesFahrenheit, ratioToPercent, friendlyName } from 'helpers/formatters'

import { color } from 'styles/colors'

const propTypes = {
  mode: PropTypes.string,
  coolSetpoint: PropTypes.string,
  heatSetpoint: PropTypes.string,
  indoorTemperature: PropTypes.string,
  relativeHumidity: PropTypes.number,
  outdoorTemperature: PropTypes.string,
  hasAwairDevice: PropTypes.bool,
  displayOperationStatus: PropTypes.bool,
  operationStatus: PropTypes.string,
  className: PropTypes.string
}

const defaultProps = {
  mode: null,
  coolSetpoint: null,
  heatSetpoint: null,
  indoorTemperature: null,
  relativeHumidity: null,
  outdoorTemperature: null,
  hasAwairDevice: false,
  displayOperationStatus: false,
  operationStatus: null,
  className: null
}

const OperationStatusFriendlyNames = {
  UNKNOWN: '',
  OFF: 'Off',
  IDLE: 'Idle',
  WAITING: 'Waiting',
  COOLING: 'Cooling',
  HEATING: 'Heating',
  FAN_RUNNING: 'Fan Running',
  EMERGENCY_HEATING: 'EM Heat',
  AUX_HEATING: 'Aux Heat',
  TEST_IN_PROGRESS: 'Test in Progress',
  SERVICE_REQUIRED: 'Service Required',
  COOL_RELIEVING_AIR: 'Cool Relieving',
  HEAT_RELIEVING_AIR: 'Heat Relieving',
  EM_HEAT_RELIEVING_AIR: 'EM Heat Relieving',
  AUX_HEAT_RELIEVING_AIR: 'Aux Heat Relieving'
}

const ThermostatBox = ({ mode, coolSetpoint, heatSetpoint, indoorTemperature, relativeHumidity, outdoorTemperature, hasAwairDevice, displayOperationStatus, operationStatus, className }) => {
  return (
    <ColumnGridPanel.Column {...{ className }}>
      <S.ThermostatBox>
        <S.Row>
          <S.IndoorTemperatureContainer>
            <S.IndoorTemperature>
              {degrees(indoorTemperature, 1) || '--'}
            </S.IndoorTemperature>
            <S.RelativeHumidity>
              <i className="icon-droplet" />
              {ratioToPercent(relativeHumidity) || '--'}
            </S.RelativeHumidity>

            {displayOperationStatus && <S.OperationStatus>{friendlyName(operationStatus, OperationStatusFriendlyNames)}</S.OperationStatus>}
          </S.IndoorTemperatureContainer>
          <S.Setpoints>
            {mode && (mode.match(/COOL/gi) || mode.match(/AUTO/gi)) && <S.CoolSetpoint>{degrees(coolSetpoint) || '--'}</S.CoolSetpoint>}
            {mode && (mode.match(/HEAT/gi) || mode.match(/AUTO/gi)) && <S.HeatSetpoint>{degrees(heatSetpoint) || '--'}</S.HeatSetpoint>}
          </S.Setpoints>
        </S.Row>
        <S.OutdoorTemperature>
          <S.DisplayLabel>Outdoor Temp</S.DisplayLabel>
          <S.DisplayValue>{degreesFahrenheit(outdoorTemperature) || '--'}</S.DisplayValue>
        </S.OutdoorTemperature>
      </S.ThermostatBox>

      <S.SystemBadges>
        { hasAwairDevice && <S.AwairIcon src="/img/awair_badge.svg" alt="awair-icon" aria-label="awair icon" /> }
      </S.SystemBadges>
    </ColumnGridPanel.Column>
  )
}

ThermostatBox.propTypes = propTypes
ThermostatBox.defaultProps = defaultProps

export default ThermostatBox

const setpointStyle = () => {
  return css`
    font-weight: bold;
    padding: 2px 5px;
  `
}

const S = {
  ThermostatBox: styled.div`
    width: 150px;
    border-radius: 10px;
    background: ${color('thermostatBoxBackground')};
    text-align: center;
    color: ${color('thermostatBoxForeground')};
    margin: 40px auto 10px auto;
  `,
  Row: styled.div`
    display: flex;
    align-items: center;
    padding-top: 10px;
  `,
  IndoorTemperatureContainer: styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    align-items: center;
  `,
  IndoorTemperature: styled.div`
    font-size: 2.0rem;
    font-weight: bold;
  `,
  RelativeHumidity: styled.div`
    font-size: 0.75rem;

    .icon-droplet {
      color: ${color('iconDropletBackground')};
    }

    &:after {
      content: ' RH';
      font-size: 0.75rem;
    }
  `,
  OperationStatus: styled.div`
    font-size: 0.75rem;
  `,
  Setpoints: styled.div`
    margin-right: 5px;
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
  CoolSetpoint: styled.div`
    ${setpointStyle}
    background: ${color('thermostatBoxCoolSetpoint')};
  `,
  HeatSetpoint: styled.div`
    ${setpointStyle}
    background: ${color('thermostatBoxHeatSetpoint')};
  `,
  OutdoorTemperature: styled.div`
    text-transform: capitalize;
    font-size: 0.75rem;
    padding-top: 5px;
    padding-bottom: 5px;
    white-space: nowrap;
    text-align: center;
    display: grid;
    grid-template-columns: auto auto;
    grid-gap: 10px;
  `,
  DisplayLabel: styled.div`
    text-align: right;
  `,
  DisplayValue: styled.div`
    text-align: left;
  `,
  SystemBadges: styled.div`
    display: flex;
  `,
  AwairIcon: styled.img`
    width: 100%;
    max-width: 138px;
    margin: 0px auto;
  `
}
